import React, {
  createContext, useContext, useRef, useState
} from "react";
import {
  useGalaxyCategoryOrderQuery,
  useSaveGalaxyCategoryOrderMutation
} from "../../../Services/CategoryApi";

const OrderContext = createContext(null);

export default function withGalaxyCategoryOrder(Page) {
  return function OrderedCategoryPage(props) {
    const query = useGalaxyCategoryOrderQuery(undefined, {
      refetchOnMountOrArgChange: true
    });
    const [saveOrder, { isLoading: saving }] =
      useSaveGalaxyCategoryOrderMutation();

    const [draft, setDraft] = useState(null);
    const [message, setMessage] = useState("");

    const list = draft?.categories || query.data?.categories || [];
    const busy = query.isFetching || saving;
    const enabled =
      !busy && !query.error &&
      Array.isArray(query.data?.categories) &&
      Number.isSafeInteger(query.data?.revision);

    function move(id, position) {
      if (!enabled) return;

      const from = list.findIndex(c => String(c._id) === id);
      if (from < 0 || !Number.isInteger(position)) return;

      const to = Math.max(0, Math.min(position, list.length - 1));
      if (from === to) return;

      const next = list.slice();
      next.splice(to, 0, next.splice(from, 1)[0]);

      setDraft({
        categories: next,
        revision: draft?.revision ?? query.data.revision
      });
      setMessage("Order changed. Press Save Order to publish.");
    }

    async function save() {
      if (!enabled || !draft) return;

      try {
        await saveOrder({
          ids: draft.categories.map(c => c._id),
          revision: draft.revision
        }).unwrap();

        setDraft(null);
        setMessage("Category order saved.");
      } catch (error) {
        setMessage(
          error?.data?.message ||
          "Save failed. Changes have not been confirmed."
        );
      }
    }

    async function reload() {
      if (busy) return;
      try {
        await query.refetch().unwrap();
        setDraft(null);
        setMessage("");
      } catch {
        setMessage("Could not reload the category order.");
      }
    }

    // No separate panel outside the existing page layout.
    return (
      <OrderContext.Provider value={{
        list, enabled, busy, dirty: !!draft, message,
        error: query.error, move, save, reload
      }}>
        <Page {...props} />
      </OrderContext.Provider>
    );
  };
}

export function GalaxyCategoryToolbar() {
  const order = useContext(OrderContext);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap"
      }}>
        <span>Drag the ⋮⋮ handle to reorder categories.</span>

        <button
          type="button"
          className="btn btn-primary"
          onClick={order.save}
          disabled={!order.enabled || !order.dirty}
        >
          {order.busy ? "Loading…" : "Save Order"}
        </button>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={order.reload}
          disabled={order.busy}
        >
          {order.dirty ? "Discard changes" : "Reload"}
        </button>
      </div>

      <div role="status" style={{ marginTop: 6 }}>
        {order.error
          ? "Category ordering could not load. Try Reload."
          : order.message}
      </div>
    </div>
  );
}

export function GalaxyCategoryRows({ rows, renderRow }) {
  const order = useContext(OrderContext);
  const dragging = useRef("");
  const [over, setOver] = useState("");
  const [chooser, setChooser] = useState("");

  const ranks = new Map(
    order.list.map((c, index) => [String(c._id), index])
  );

  const visible = (Array.isArray(rows) ? rows : [])
    .slice()
    .sort((a, b) =>
      (ranks.get(String(a._id)) ?? 1e9) -
      (ranks.get(String(b._id)) ?? 1e9)
    );

  return <>{visible.map((category, index) => {
    const row = renderRow(category, index);
    if (!React.isValidElement(row) || row.type !== "tr") return row;

    const id = String(category._id);
    const position = ranks.get(id);
    const disabled = !order.enabled || position === undefined;

    const handle = (
      <td
        key="galaxy-reorder-handle"
        style={{ width: 48, verticalAlign: "middle" }}
      >
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={disabled}
          draggable={!disabled}
          style={{
            cursor: disabled ? "default" : "grab",
            minWidth: 36, minHeight: 36, fontSize: 20
          }}
          aria-label={"Reorder " + category.name}
          title="Drag to reorder; click to choose a position. Arrow keys also work."
          onDragStart={e => {
            dragging.current = id;
            setChooser("");
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", id);
            const tr = e.currentTarget.closest("tr");
            if (tr) e.dataTransfer.setDragImage(tr, 20, 20);
          }}
          onDragEnd={() => {
            dragging.current = "";
            setOver("");
          }}
          onClick={e => {
            e.stopPropagation();
            setChooser(chooser === id ? "" : id);
          }}
          onKeyDown={e => {
            const delta =
              e.key === "ArrowUp" ? -1 :
              e.key === "ArrowDown" ? 1 : 0;
            if (delta) {
              e.preventDefault();
              order.move(id, position + delta);
            }
          }}
        >
          ⋮⋮
        </button>

        {chooser === id && (
          <select
            autoFocus
            className="form-select form-select-sm"
            style={{ minWidth: 145, marginTop: 6 }}
            aria-label={"New position for " + category.name}
            value={position}
            onClick={e => e.stopPropagation()}
            onChange={e => {
              order.move(id, Number(e.target.value));
              setChooser("");
            }}
            onBlur={() => setChooser("")}
          >
            {order.list.map((c, i) => (
              <option key={c._id} value={i}>
                {i + 1}. {c.name}
              </option>
            ))}
          </select>
        )}
      </td>
    );

    return React.cloneElement(row, {
      key: id,
      onDragOver: e => {
        if (!dragging.current || disabled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(id);
      },
      onDrop: e => {
        e.preventDefault();
        e.stopPropagation();
        if (dragging.current && !disabled) {
          order.move(dragging.current, position);
        }
        dragging.current = "";
        setOver("");
      },
      style: {
        ...row.props.style,
        outline: over === id
          ? "2px solid #148461"
          : row.props.style?.outline
      }
    }, [handle, ...React.Children.toArray(row.props.children)]);
  })}</>;
}
